import { useNavigate } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { projects } from '@/mocks/projects';

export default function FeaturedProjects() {
  const navigate = useNavigate();
  const { ref, isVisible } = useScrollReveal();
  const featured = projects.filter((p) => p.featured);

  return (
    <section id="projects" className="w-full py-16 md:py-24 bg-ivory-50">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div
          ref={ref}
          className={`flex flex-col md:flex-row items-start md:items-end justify-between gap-4 mb-10 md:mb-14 ${
            isVisible ? 'animate-fade-in-up' : 'opacity-0'
          }`}
        >
          <div>
            <span className="inline-block text-xs md:text-sm font-semibold text-brand-orange uppercase tracking-widest mb-3">
              Investment Opportunities
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-charcoal-300">
              Featured Projects
            </h2>
          </div>
          <button
            onClick={() => { navigate('/projects'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="flex items-center gap-2 text-sm font-semibold text-brand-orange hover:text-brand-orangeLight transition-colors whitespace-nowrap"
          >
            View All Projects
            <i className="ri-arrow-right-line" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {featured.map((project, index) => (
            <div
              key={project.id}
              className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 cursor-pointer ${
                isVisible ? 'animate-fade-in-up' : 'opacity-0'
              }`}
              style={{ animationDelay: `${0.15 + index * 0.12}s` }}
              onClick={() => { navigate(`/projects/${project.id}`); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              {/* Image */}
              <div className="relative h-52 md:h-56 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal-400/60 via-transparent to-transparent" />
                <div className="absolute top-3 left-3">
                  <span className="px-3 py-1 bg-brand-orange text-white text-xs font-semibold rounded-full">
                    {project.category}
                  </span>
                </div>
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="flex items-center gap-1.5 text-white text-sm">
                    <i className="ri-map-pin-line" />
                    {project.location}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 md:p-5">
                <h3 className="text-base md:text-lg font-bold text-charcoal-300 leading-snug mb-2 group-hover:text-brand-orange transition-colors">
                  {project.title}
                </h3>
                <p className="text-sm text-charcoal-50 leading-relaxed mb-4 line-clamp-2">
                  {project.description}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.plotSizes.slice(0, 3).map((size) => (
                    <span
                      key={size}
                      className="px-2.5 py-1 bg-ivory-100 text-charcoal-100 text-xs font-medium rounded-md"
                    >
                      {size}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-ivory-200">
                  <span className="text-sm font-semibold text-brand-orange">
                    From {project.priceFrom}
                  </span>
                  <span className="flex items-center gap-1 text-sm font-medium text-charcoal-100 group-hover:text-brand-orange transition-colors">
                    View Details
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}